import { Component, Input, ChangeDetectorRef, inject } from '@angular/core';
import { LogError, Metadata, RunView } from '@memberjunction/core';
import {
    marketingExperimentEntity,
    marketingSprintEntity,
    marketingSprintExperimentEntity,
} from 'mj_generatedentities';
import { runAction, parseActionMessageJson, extractRecFromMessage, isRecommendDecisionResp } from '../../shared/run-action.util';

interface SprintCapacityTier {
    tier?: string;
    name?: string;
    free?: number;
    available?: number;
    total?: number;
    capacity?: number;
}

interface SprintCapacityResp {
    sprintLabel?: string;
    sprintNumber?: number;
    tiers?: SprintCapacityTier[];
}

function isSprintCapacityResp(v: unknown): v is SprintCapacityResp {
    if (typeof v !== 'object' || v === null) return false;
    const r = v as Record<string, unknown>;
    return r['tiers'] === undefined || Array.isArray(r['tiers']);
}

function parseSavedMidInput(raw: string | null | undefined): { observedMetric: number; conversionsCount: number; recordedAt: string } | null {
    if (!raw) return null;
    try {
        const obj: unknown = JSON.parse(raw);
        if (typeof obj !== 'object' || obj === null) return null;
        const o = obj as { observedMetric?: unknown; conversionsCount?: unknown; recordedAt?: unknown };
        const observedMetric = typeof o.observedMetric === 'number' ? o.observedMetric : Number(o.observedMetric);
        const conversionsCount = typeof o.conversionsCount === 'number' ? o.conversionsCount : Number(o.conversionsCount);
        if (!Number.isFinite(observedMetric) || !Number.isFinite(conversionsCount)) return null;
        return {
            observedMetric,
            conversionsCount,
            recordedAt: typeof o.recordedAt === 'string' ? o.recordedAt : '',
        };
    } catch {
        return null;
    }
}

interface MidSprintInput {
    observedMetric: number | null;
    conversionsCount: number | null;
    saving: boolean;
    savedAt: string | null;
    error: string;
}

interface DueRow {
    experiment: marketingExperimentEntity;
    rec: { decision: string; rationale: string; confidence: string; nextActionHint: string } | null;
    busy: boolean;
    busyMessage: string;
    busyError: boolean;
    mid: MidSprintInput;
    /** Pending decision the user picked but hasn't confirmed (because it overrides the agent rec). */
    pendingDecision: 'KEEP' | 'ITERATE' | 'KILL' | 'GRADUATE' | null;
    /** Captured reason text when user is overriding the agent's recommendation. */
    overrideReason: string;
}

@Component({
    standalone: false,
    selector: 'mkt-ws-experiments-sprint-review',
    template: `
<mkt-ws-page-head
    [eyebrow]="headerEyebrow"
    title="Decide before the sprint closes."
    sub="Experiments that have hit their decision date. Each row shows the agent's recommendation; you can override."
    [hasActions]="true">
    <div actions>
        <button class="sr__btn" type="button" (click)="navigate('experiments')"><i class="fa-solid fa-arrow-left"></i> Back</button>
        <button class="sr__btn sr__btn--primary" type="button" [disabled]="bulkBusy || dueRows.length === 0" (click)="acceptAll()">
            <i class="fa-solid fa-check-double"></i> {{ bulkBusy ? 'Applying…' : 'Accept all agent recs' }}
        </button>
    </div>
</mkt-ws-page-head>

<div *ngIf="loading" class="sr__loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading sprint review…</div>

<ng-container *ngIf="!loading">
    <!-- Sprint context -->
    <div class="sr__totals">
        <mkt-ws-card>
            <div class="sr__caps">Decisions due</div>
            <div class="sr__big">{{ dueRows.length }}</div>
            <div class="sr__sub">{{ sprintEndLabel }}</div>
        </mkt-ws-card>
        <mkt-ws-card>
            <div class="sr__caps">Active rolling</div>
            <div class="sr__big">{{ activeRolling }}</div>
            <div class="sr__sub">Continue next sprint</div>
        </mkt-ws-card>
        <mkt-ws-card>
            <div class="sr__caps">Proposed for next</div>
            <div class="sr__big">{{ proposedCount }}</div>
            <div class="sr__sub">Tier 1 → Tier 3</div>
        </mkt-ws-card>
        <mkt-ws-card>
            <div class="sr__caps">Slots open next sprint</div>
            <div class="sr__big">{{ totalFreeSlots }} / {{ totalSlots }}</div>
            <div class="sr__sub">WIP cap holding</div>
        </mkt-ws-card>
    </div>

    <!-- Sprint plan -->
    <mkt-ws-card class="sr__sec" [hasHead]="true" *ngIf="currentSprint">
        <div head>
            <span class="sr__caps-ink">Sprint plan</span>
            <span class="sr__sub">What we're testing · tier breakdown · decisions due · risks</span>
        </div>
        <div *ngIf="!planEditing && !planDoc" class="sr__plan-empty">
            <div class="sr__sub" style="margin-bottom: 10px;">No plan drafted yet for this sprint.</div>
            <button class="sr__btn sr__btn--primary" type="button" [disabled]="planBusy" (click)="generatePlan()">
                <i class="fa-solid fa-wand-magic-sparkles"></i> {{ planBusy ? 'Drafting…' : 'Generate sprint plan' }}
            </button>
            <div *ngIf="planError" class="sr__row-msg sr__row-msg--err">{{ planError }}</div>
        </div>
        <div *ngIf="!planEditing && planDoc" class="sr__plan-view">
            <pre class="sr__plan-md">{{ planDoc }}</pre>
            <div class="sr__plan-actions">
                <button class="sr__btn" type="button" [disabled]="planBusy" (click)="planEditing = true; planDraft = planDoc">
                    <i class="fa-solid fa-pen"></i> Edit
                </button>
                <button class="sr__btn" type="button" [disabled]="planBusy" (click)="generatePlan()">
                    <i class="fa-solid fa-rotate"></i> {{ planBusy ? 'Re-drafting…' : 'Re-generate' }}
                </button>
            </div>
        </div>
        <div *ngIf="planEditing" class="sr__plan-edit">
            <textarea class="sr__textarea sr__textarea--lg" rows="14" [(ngModel)]="planDraft"></textarea>
            <div class="sr__plan-actions">
                <button class="sr__btn sr__btn--primary" type="button" [disabled]="planBusy" (click)="savePlan()">
                    <i class="fa-solid fa-save"></i> {{ planBusy ? 'Saving…' : 'Save' }}
                </button>
                <button class="sr__btn" type="button" [disabled]="planBusy" (click)="planEditing = false">Cancel</button>
            </div>
            <div *ngIf="planError" class="sr__row-msg sr__row-msg--err">{{ planError }}</div>
        </div>
    </mkt-ws-card>

    <!-- Decisions due -->
    <mkt-ws-card class="sr__sec" [hasHead]="true" [noPadding]="true">
        <div head>
            <div style="display:flex; align-items:center; gap:8px;">
                <span class="sr__caps-ink">Decisions due</span>
                <mkt-ws-pill *ngIf="dueRows.length > 0" variant="warn">{{ dueRows.length }}</mkt-ws-pill>
            </div>
            <span class="sr__sub">Agent recommendation shown · you can override</span>
        </div>
        <div *ngIf="dueRows.length === 0" class="sr__empty">No experiments have hit their decision date in this sprint window.</div>
        <div *ngFor="let row of dueRows" class="sr__row">
            <div class="sr__row-head">
                <div>
                    <div class="sr__caps">{{ row.experiment.Code || '—' }} · {{ row.experiment.ExperimentTier || '—' }}</div>
                    <div class="sr__row-title">{{ row.experiment.Name || row.experiment.Hypothesis }}</div>
                </div>
                <mkt-ws-pill *ngIf="row.rec?.decision" [variant]="recVariant(row.rec?.decision)">Agent: {{ row.rec?.decision }}</mkt-ws-pill>
            </div>
            <div class="sr__row-rationale" *ngIf="row.rec?.rationale">{{ row.rec?.rationale }}</div>
            <div *ngIf="row.rec?.nextActionHint" class="sr__row-hint">
                <i class="fa-solid fa-lightbulb"></i>
                <strong>Next move:</strong> {{ row.rec?.nextActionHint }}
            </div>
            <div class="sr__mid">
                <div class="sr__mid-head">
                    <span class="sr__caps">Observed this sprint</span>
                    <span *ngIf="row.mid.savedAt" class="sr__sub">Saved {{ formatSavedTimestamp(row.mid.savedAt) }}</span>
                </div>
                <div class="sr__mid-fields">
                    <label>
                        <span class="sr__caps">Observed metric</span>
                        <input type="number" step="any" class="sr__input" [(ngModel)]="row.mid.observedMetric" placeholder="e.g., 1.6" />
                    </label>
                    <label>
                        <span class="sr__caps">Conversions count</span>
                        <input type="number" step="1" class="sr__input" [(ngModel)]="row.mid.conversionsCount" placeholder="e.g., 18" />
                    </label>
                    <button class="sr__btn" type="button" [disabled]="row.mid.saving" (click)="saveMidSprintInput(row)">
                        <i class="fa-solid fa-save"></i> {{ row.mid.saving ? 'Saving…' : 'Save' }}
                    </button>
                </div>
                <div *ngIf="row.mid.error" class="sr__row-msg sr__row-msg--err">{{ row.mid.error }}</div>
            </div>
            <div class="sr__row-actions">
                <button class="sr__btn sr__btn--primary" type="button" [disabled]="row.busy" (click)="pickDecision(row, 'KEEP')">
                    <i class="fa-solid fa-check"></i> KEEP
                </button>
                <button class="sr__btn" type="button" [disabled]="row.busy" (click)="pickDecision(row, 'ITERATE')">
                    <i class="fa-solid fa-rotate-left"></i> ITERATE
                </button>
                <button class="sr__btn" type="button" [disabled]="row.busy" (click)="pickDecision(row, 'KILL')">
                    <i class="fa-solid fa-xmark"></i> KILL
                </button>
                <button class="sr__btn" type="button" [disabled]="row.busy" (click)="pickDecision(row, 'GRADUATE')">
                    <i class="fa-solid fa-arrow-trend-up"></i> Graduate
                </button>
                <button class="sr__btn sr__btn--ghost" type="button" (click)="openDetail(row.experiment.ID)">Detail →</button>
            </div>

            <div *ngIf="row.pendingDecision" class="sr__override">
                <div class="sr__override-head">
                    <i class="fa-solid fa-circle-exclamation"></i>
                    <strong>Override the agent recommendation</strong>
                    <span class="sr__sub">You picked <b>{{ row.pendingDecision }}</b>; agent recommended <b>{{ row.rec?.decision || '—' }}</b>. Capture why so the next sprint review can learn from it.</span>
                </div>
                <textarea class="sr__textarea" rows="2" [(ngModel)]="row.overrideReason"
                    placeholder="One line: why your call beats the agent's (e.g., 'Sample too small to trust the rec; rerunning with bigger budget')."></textarea>
                <div class="sr__override-actions">
                    <button class="sr__btn sr__btn--primary" type="button" [disabled]="row.busy || !row.overrideReason.trim()" (click)="confirmDecision(row)">
                        <i class="fa-solid fa-check"></i> Confirm {{ row.pendingDecision }}
                    </button>
                    <button class="sr__btn" type="button" [disabled]="row.busy" (click)="cancelDecision(row)">Cancel</button>
                </div>
            </div>

            <div *ngIf="row.busyMessage" class="sr__row-msg" [class.sr__row-msg--err]="row.busyError">{{ row.busyMessage }}</div>
        </div>
    </mkt-ws-card>

    <!-- Sprint timeline -->
    <mkt-ws-card class="sr__sec" [hasHead]="true">
        <div head>
            <span class="sr__caps-ink">Sprint timeline</span>
            <span class="sr__sub">Last {{ timeline.length }} sprints</span>
        </div>
        <div class="sr__timeline">
            <div *ngFor="let s of timeline" class="sr__timeline-col">
                <div class="sr__timeline-head">
                    <span class="sr__caps">{{ s.label }}</span>
                    <mkt-ws-pill [variant]="timelineStatusVariant(s.status)">{{ s.status }}</mkt-ws-pill>
                </div>
                <div class="sr__timeline-pills">
                    <mkt-ws-pill *ngIf="s.keep > 0" variant="good">{{ s.keep }} KEEP</mkt-ws-pill>
                    <mkt-ws-pill *ngIf="s.iterate > 0" variant="warn">{{ s.iterate }} ITERATE</mkt-ws-pill>
                    <mkt-ws-pill *ngIf="s.kill > 0" variant="bad">{{ s.kill }} KILL</mkt-ws-pill>
                    <mkt-ws-pill *ngIf="s.graduate > 0" variant="good">{{ s.graduate }} GRAD</mkt-ws-pill>
                    <mkt-ws-pill *ngIf="s.keep + s.iterate + s.kill + s.graduate === 0" variant="muted">—</mkt-ws-pill>
                </div>
            </div>
        </div>
    </mkt-ws-card>
</ng-container>
    `,
    styles: [`
        :host { display: block; }
        .sr__loading { padding: 60px 20px; text-align: center; color: var(--mj-text-secondary, #71717a); }

        .sr__btn { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; height: 30px; border: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-bg-surface, #fff); border-radius: 6px; font: inherit; font-size: 12px; cursor: pointer; color: var(--mj-text-primary, #1a1a1a); }
        .sr__btn:hover { background: var(--mj-bg-page, #f5f5f5); }
        .sr__btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .sr__btn--primary { background: var(--mj-text-primary, #1a1a1a); color: var(--mj-bg-surface, #fff); border-color: var(--mj-text-primary, #1a1a1a); }
        .sr__btn--ghost { margin-left: auto; }

        .sr__caps, .sr__caps-ink { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); }
        .sr__caps-ink { color: var(--mj-text-primary, #1a1a1a); }
        .sr__sub { font-size: 11.5px; color: var(--mj-text-secondary, #71717a); }
        .sr__big { font-family: ui-monospace, monospace; font-variant-numeric: tabular-nums; font-size: 22px; color: var(--mj-text-primary, #1a1a1a); margin: 4px 0 2px; }

        .sr__sec { margin-bottom: 24px; }
        .sr__totals { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 24px; }

        .sr__input { padding: 6px 10px; height: 32px; font: inherit; font-size: 12.5px; border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 6px; background: var(--mj-bg-surface, #fff); color: var(--mj-text-primary, #1a1a1a); width: 110px; }
        .sr__input:focus { outline: 2px solid var(--mj-brand-primary, #6366f1); outline-offset: -1px; }
        .sr__mid { margin: 12px 0; padding: 10px 12px; background: var(--mj-bg-page, #f5f5f5); border-radius: 6px; }
        .sr__mid-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
        .sr__mid-fields { display: flex; gap: 12px; align-items: flex-end; flex-wrap: wrap; }
        .sr__mid-fields label { display: flex; flex-direction: column; gap: 4px; }
        .sr__timeline-head { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
        .sr__row { padding: 18px 20px; border-bottom: 1px solid var(--mj-border-default, #e0e0e0); }
        .sr__row:last-child { border-bottom: none; }
        .sr__row-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 8px; }
        .sr__row-title { font-size: 14px; font-weight: 500; color: var(--mj-text-primary, #1a1a1a); margin-top: 4px; line-height: 1.4; }
        .sr__row-rationale { font-size: 12.5px; color: var(--mj-text-secondary, #71717a); margin-bottom: 10px; line-height: 1.5; }
        .sr__row-hint { margin-bottom: 10px; padding: 6px 10px; background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-left: 3px solid var(--mj-color-warning-700, #b45309); border-radius: 6px; font-size: 12px; color: var(--mj-text-primary, #1a1a1a); display: flex; align-items: center; gap: 6px; }
        .sr__row-hint .fa-lightbulb { color: var(--mj-color-warning-700, #b45309); }
        .sr__row-actions { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
        .sr__row-msg { margin-top: 8px; font-size: 11.5px; color: var(--mj-text-secondary, #71717a); }
        .sr__row-msg--err { color: var(--mj-status-error, #be123c); }
        .sr__empty { padding: 32px; text-align: center; font-size: 12.5px; color: var(--mj-text-secondary, #71717a); }

        .sr__timeline { display: grid; grid-template-columns: repeat(6, 1fr); gap: 12px; }
        .sr__timeline-col { display: flex; flex-direction: column; gap: 4px; }
        .sr__timeline-pills { display: flex; flex-direction: column; gap: 4px; align-items: flex-start; }

        .sr__override { margin-top: 10px; padding: 12px 14px; background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-left: 3px solid var(--mj-color-warning-700, #b45309); border-radius: 6px; color: var(--mj-text-primary, #1a1a1a); }
        .sr__override-head { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; font-size: 12.5px; color: var(--mj-text-primary, #1a1a1a); flex-wrap: wrap; }
        .sr__override-head .fa-circle-exclamation { color: var(--mj-color-warning-700, #b45309); }
        .sr__override-actions { display: flex; gap: 6px; margin-top: 8px; }
        .sr__textarea { width: 100%; box-sizing: border-box; padding: 8px 10px; font: inherit; font-size: 12.5px; border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 6px; background: var(--mj-bg-surface, #fff); color: var(--mj-text-primary, #1a1a1a); resize: vertical; min-height: 48px; }
        .sr__textarea:focus { outline: 2px solid var(--mj-brand-primary, #6366f1); outline-offset: -1px; }
        .sr__textarea--lg { min-height: 240px; font-family: ui-monospace, monospace; font-size: 12px; }

        .sr__plan-empty { padding: 8px 0; }
        .sr__plan-md { white-space: pre-wrap; font-family: ui-monospace, monospace; font-size: 12px; line-height: 1.55; padding: 12px 14px; background: var(--mj-bg-page, #f5f5f5); border-radius: 6px; max-height: 420px; overflow: auto; margin: 0 0 10px; }
        .sr__plan-actions { display: flex; gap: 6px; margin-top: 8px; }

        @media (max-width: 1024px) {
            .sr__totals { grid-template-columns: repeat(2, 1fr); }
            .sr__timeline { grid-template-columns: repeat(3, 1fr); }
        }
    `]
})
export class ExperimentsSprintReviewSection {
    private _companyId = '';
    @Input() set companyId(v: string) {
        if (v && v !== this._companyId) {
            this._companyId = v;
            void this.load();
        } else if (!v) {
            this._companyId = '';
        }
    }
    get companyId(): string { return this._companyId; }

    @Input() navigate: (path: string) => void = () => {};
    @Input() navigateWithId: (path: string, id?: string) => void = () => {};

    private cdr = inject(ChangeDetectorRef);

    loading = false;
    currentSprint: marketingSprintEntity | null = null;
    dueRows: DueRow[] = [];
    activeRolling = 0;
    proposedCount = 0;
    totalSlots = 0;
    totalFreeSlots = 0;
    timeline: { label: string; keep: number; iterate: number; kill: number; graduate: number; status: string }[] = [];
    bulkBusy = false;

    planDoc = '';
    planDraft = '';
    planEditing = false;
    planBusy = false;
    planError = '';

    get headerEyebrow(): string {
        if (!this.currentSprint) return 'Experiments · Sprint review';
        const range = `${new Date(this.currentSprint.StartDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} → ${new Date(this.currentSprint.EndDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
        return `Experiments · Sprint ${this.currentSprint.SprintNumber || ''} · ${range}`;
    }

    get sprintEndLabel(): string {
        if (!this.currentSprint) return '';
        return new Date(this.currentSprint.EndDate).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    }

    recVariant(decision: string | undefined | null): 'good' | 'warn' | 'bad' | 'muted' {
        const d = (decision || '').toLowerCase();
        if (d.includes('keep') || d.includes('graduate')) return 'good';
        if (d.includes('iterate') || d.includes('hold')) return 'warn';
        if (d.includes('kill')) return 'bad';
        return 'muted';
    }

    timelineStatusVariant(status: string | undefined | null): 'good' | 'warn' | 'bad' | 'muted' {
        const s = (status || '').toLowerCase();
        if (s === 'active') return 'good';
        if (s === 'review') return 'warn';
        if (s === 'completed') return 'muted';
        return 'muted';
    }

    formatSavedTimestamp(iso: string | null): string {
        if (!iso) return '';
        try {
            return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
        } catch {
            return '';
        }
    }

    openDetail(id: string): void {
        this.navigateWithId('experiments/detail', id);
    }

    private async load(): Promise<void> {
        if (!this._companyId) return;
        const cid = this._companyId;
        this.loading = true;
        this.cdr.detectChanges();
        try {
            const rv = new RunView();
            const [sprintRes, expRes] = await rv.RunViews([
                {
                    EntityName: 'Sprints',
                    ExtraFilter: `CompanyID='${cid}'`,
                    OrderBy: 'StartDate DESC',
                    MaxRows: 8,
                    ResultType: 'entity_object',
                },
                {
                    EntityName: 'Experiments',
                    ExtraFilter: `CompanyID='${cid}'`,
                    OrderBy: 'DecisionDate ASC',
                    MaxRows: 200,
                    ResultType: 'entity_object',
                },
            ]);
            const sprints = sprintRes.Success ? (sprintRes.Results || []) as marketingSprintEntity[] : [];
            const experiments = expRes.Success ? (expRes.Results || []) as marketingExperimentEntity[] : [];

            this.currentSprint = sprints.find(s => s.Status === 'Active' || s.Status === 'Review') || sprints[0] || null;

            // Decisions due: Active experiments whose DecisionDate falls inside or before the current sprint's end.
            const sprintEnd = this.currentSprint ? new Date(this.currentSprint.EndDate).getTime() : Date.now() + 14 * 24 * 60 * 60 * 1000;
            const due = experiments.filter(e => {
                if (e.ExperimentStatus !== 'Active') return false;
                if (!e.DecisionDate) return false;
                return new Date(e.DecisionDate).getTime() <= sprintEnd;
            });
            this.activeRolling = experiments.filter(e => e.ExperimentStatus === 'Active' && !due.includes(e)).length;
            this.proposedCount = experiments.filter(e => e.ExperimentStatus === 'Proposed').length;

            // Pre-load any saved MidSprintMetricInput from the linkage rows for the current sprint.
            const linkRows: marketingSprintExperimentEntity[] = this.currentSprint
                ? await this.loadSprintExperimentLinks(this.currentSprint.ID)
                : [];

            this.dueRows = due.map(e => {
                const link = linkRows.find(l => l.ExperimentID === e.ID);
                const saved = parseSavedMidInput(link?.MidSprintMetricInput);
                return {
                    experiment: e,
                    rec: null,
                    busy: false,
                    busyMessage: '',
                    busyError: false,
                    mid: {
                        observedMetric: saved?.observedMetric ?? null,
                        conversionsCount: saved?.conversionsCount ?? null,
                        saving: false,
                        savedAt: saved?.recordedAt ?? null,
                        error: '',
                    },
                    pendingDecision: null,
                    overrideReason: '',
                };
            });

            this.planDoc = this.currentSprint?.PlanningDoc ?? '';
            this.planEditing = false;
            this.planError = '';

            // Capacity
            const capRes = await runAction('Get Sprint Slot Capacity', [
                { Name: 'CompanyID', Value: cid },
            ]);
            if (capRes.success) {
                const parsed = parseActionMessageJson(capRes.message, isSprintCapacityResp);
                if (parsed?.tiers) {
                    this.totalSlots = parsed.tiers.reduce((s, t) => s + (t.total ?? t.capacity ?? 0), 0);
                    this.totalFreeSlots = parsed.tiers.reduce((s, t) => s + (t.free ?? t.available ?? 0), 0);
                }
            }

            // Timeline — bucket Decided experiments by sprint window
            this.timeline = this.buildTimeline(sprints, experiments);

            // Fetch recommendations in parallel
            await this.loadRecommendations();
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[ExperimentsSprintReview] load failed: ${msg}`);
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }

    private async loadSprintExperimentLinks(sprintId: string): Promise<marketingSprintExperimentEntity[]> {
        const rv = new RunView();
        const res = await rv.RunView({
            EntityName: 'Sprint Experiments',
            ExtraFilter: `SprintID='${sprintId}'`,
            ResultType: 'entity_object',
        });
        if (!res.Success) {
            LogError(`[ExperimentsSprintReview] sprint links load failed: ${res.ErrorMessage}`);
            return [];
        }
        return (res.Results || []) as marketingSprintExperimentEntity[];
    }

    /**
     * Save the founder's mid-sprint metric input to the SprintExperiment
     * linkage row. Pure entity Save — no action round-trip needed.
     */
    async saveMidSprintInput(row: DueRow): Promise<void> {
        if (!this.currentSprint) return;
        if (row.mid.observedMetric == null || row.mid.conversionsCount == null) {
            row.mid.error = 'Both metric and conversions are required.';
            this.cdr.detectChanges();
            return;
        }
        row.mid.saving = true;
        row.mid.error = '';
        this.cdr.detectChanges();

        try {
            const md = new Metadata();
            const rv = new RunView();
            const linkRes = await rv.RunView({
                EntityName: 'Sprint Experiments',
                ExtraFilter: `SprintID='${this.currentSprint.ID}' AND ExperimentID='${row.experiment.ID}'`,
                MaxRows: 1,
                ResultType: 'simple',
            });
            const linkId = linkRes.Success && linkRes.Results && linkRes.Results.length > 0
                ? (linkRes.Results[0] as { ID: string }).ID
                : null;
            const link = await md.GetEntityObject<marketingSprintExperimentEntity>('Sprint Experiments');
            if (linkId) {
                await link.Load(linkId);
            } else {
                link.NewRecord();
                link.SprintID = this.currentSprint.ID;
                link.ExperimentID = row.experiment.ID;
                link.IsAddedMidSprint = true;
            }
            const recordedAt = new Date().toISOString();
            link.MidSprintMetricInput = JSON.stringify({
                observedMetric: row.mid.observedMetric,
                conversionsCount: row.mid.conversionsCount,
                recordedAt,
            });
            const saved = await link.Save();
            if (!saved) {
                row.mid.error = `Save failed: ${link.LatestResult?.Message ?? 'unknown'}`;
            } else {
                row.mid.savedAt = recordedAt;
            }
        } catch (err: unknown) {
            row.mid.error = err instanceof Error ? err.message : String(err);
        } finally {
            row.mid.saving = false;
            this.cdr.detectChanges();
        }
    }

    private buildTimeline(sprints: marketingSprintEntity[], experiments: marketingExperimentEntity[]): { label: string; keep: number; iterate: number; kill: number; graduate: number; status: string }[] {
        const recent = [...sprints].sort((a, b) => new Date(a.StartDate).getTime() - new Date(b.StartDate).getTime()).slice(-6);
        return recent.map(s => {
            const start = new Date(s.StartDate).getTime();
            const end = new Date(s.EndDate).getTime();
            const decidedInSprint = experiments.filter(e => {
                if (!e.DecidedDate || !e.ExperimentDecision) return false;
                const d = new Date(e.DecidedDate).getTime();
                return d >= start && d <= end;
            });
            return {
                label: s.SprintNumber ? `Sp ${s.SprintNumber}` : new Date(s.StartDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
                status: s.Status || '—',
                keep: decidedInSprint.filter(e => /^keep$/i.test(e.ExperimentDecision || '')).length,
                iterate: decidedInSprint.filter(e => /iterate/i.test(e.ExperimentDecision || '')).length,
                kill: decidedInSprint.filter(e => /kill/i.test(e.ExperimentDecision || '')).length,
                graduate: decidedInSprint.filter(e => /graduate/i.test(e.ExperimentDecision || '')).length,
            };
        });
    }

    private async loadRecommendations(): Promise<void> {
        await Promise.all(this.dueRows.map(async row => {
            // Synthesize observation inputs (placeholder until CPM-by-experiment aggregation lands).
            const startDate = row.experiment.StartDate ? new Date(row.experiment.StartDate) : new Date();
            const weeksRunning = Math.max(1, Math.floor((Date.now() - startDate.getTime()) / (7 * 24 * 60 * 60 * 1000)));
            const baseline = row.experiment.MetricBaseline ?? 1;
            const observedMetric = baseline * 1.4;
            const conversionsCount = Math.max(0, Math.round(observedMetric * 10));
            const res = await runAction('Recommend Decision', [
                { Name: 'CompanyID', Value: row.experiment.CompanyID },
                { Name: 'ExperimentID', Value: row.experiment.ID },
                { Name: 'ObservedMetric', Value: String(observedMetric) },
                { Name: 'ConversionsCount', Value: String(conversionsCount) },
                { Name: 'WeeksRunning', Value: String(weeksRunning) },
            ]);
            if (res.success) {
                const parsed = parseActionMessageJson(res.message, isRecommendDecisionResp);
                if (parsed?.decision) {
                    row.rec = { decision: parsed.decision, rationale: parsed.rationale || '', confidence: parsed.confidence || '', nextActionHint: parsed.nextActionHint || '' };
                } else {
                    const fromText = extractRecFromMessage(res.message);
                    row.rec = fromText
                        ? { decision: fromText.decision, rationale: fromText.rationale, confidence: fromText.confidence, nextActionHint: '' }
                        : { decision: 'See message', rationale: res.message, confidence: '', nextActionHint: '' };
                }
            } else {
                row.rec = { decision: '', rationale: `Error: ${res.message}`, confidence: '', nextActionHint: '' };
            }
            this.cdr.detectChanges();
        }));
    }

    /**
     * User clicked a decision button. If their pick differs from the agent's
     * recommendation, surface the override reason input before committing —
     * the framework wants every override captured for the next sprint review.
     */
    pickDecision(row: DueRow, decision: 'KEEP' | 'ITERATE' | 'KILL' | 'GRADUATE'): void {
        const agentRec = (row.rec?.decision || '').toUpperCase();
        const recAvailable = agentRec.length > 0 && !agentRec.includes('SEE MESSAGE');
        if (!recAvailable || agentRec.includes(decision)) {
            void this.commitDecision(row, decision, '');
            return;
        }
        row.pendingDecision = decision;
        row.overrideReason = '';
        this.cdr.detectChanges();
    }

    confirmDecision(row: DueRow): void {
        if (!row.pendingDecision || !row.overrideReason.trim()) return;
        const decision = row.pendingDecision;
        const reason = row.overrideReason.trim();
        row.pendingDecision = null;
        void this.commitDecision(row, decision, reason);
    }

    cancelDecision(row: DueRow): void {
        row.pendingDecision = null;
        row.overrideReason = '';
        this.cdr.detectChanges();
    }

    private async commitDecision(row: DueRow, decision: 'KEEP' | 'ITERATE' | 'KILL' | 'GRADUATE', overrideReason: string): Promise<void> {
        if (decision === 'GRADUATE') {
            await this.graduate(row);
            return;
        }
        await this.decide(row, decision, overrideReason);
    }

    async decide(row: DueRow, decision: string, overrideReason = ''): Promise<void> {
        row.busy = true;
        row.busyError = false;
        row.busyMessage = `Recording ${decision}…`;
        this.cdr.detectChanges();
        // Action contract is PascalCase: Keep / Iterate / Kill.
        const decisionName = decision.charAt(0).toUpperCase() + decision.slice(1).toLowerCase();
        const params: { Name: string; Value: string }[] = [
            { Name: 'CompanyID', Value: row.experiment.CompanyID },
            { Name: 'ExperimentID', Value: row.experiment.ID },
            { Name: 'DecisionName', Value: decisionName },
        ];
        if (row.mid.observedMetric != null && row.mid.conversionsCount != null) {
            const startDate = row.experiment.StartDate ? new Date(row.experiment.StartDate) : new Date();
            const weeksRunning = Math.max(1, Math.floor((Date.now() - startDate.getTime()) / (7 * 24 * 60 * 60 * 1000)));
            params.push({ Name: 'ObservedMetric', Value: String(row.mid.observedMetric) });
            params.push({ Name: 'ConversionsCount', Value: String(row.mid.conversionsCount) });
            params.push({ Name: 'WeeksRunning', Value: String(weeksRunning) });
        }
        if (overrideReason) params.push({ Name: 'OverrideReason', Value: overrideReason });
        const res = await runAction('Decide Experiment', params);
        if (res.success) {
            // DecideExperimentAction returns a message that already includes the
            // writeback summary (sections bumped / learnings / recommendations
            // created from ExperimentUpdateTarget rules), so display that
            // verbatim — founders see the loop close visibly.
            row.busyMessage = res.message || `Decided ${decision}.`;
            await this.load();
        } else {
            row.busyError = true;
            row.busyMessage = `Decide failed (${res.resultCode}): ${res.message}`;
        }
        row.busy = false;
        this.cdr.detectChanges();
    }

    async graduate(row: DueRow): Promise<void> {
        row.busy = true;
        row.busyError = false;
        row.busyMessage = 'Graduating to Evergreen…';
        this.cdr.detectChanges();
        const res = await runAction('Complete Experiment', [
            { Name: 'CompanyID', Value: row.experiment.CompanyID },
            { Name: 'ExperimentID', Value: row.experiment.ID },
        ]);
        if (res.success) {
            row.busyMessage = `Graduated.`;
            await this.load();
        } else {
            row.busyError = true;
            row.busyMessage = `Graduate failed (${res.resultCode}): ${res.message}`;
        }
        row.busy = false;
        this.cdr.detectChanges();
    }

    async acceptAll(): Promise<void> {
        if (this.dueRows.length === 0) return;
        this.bulkBusy = true;
        this.cdr.detectChanges();
        for (const row of this.dueRows) {
            const decision = (row.rec?.decision || '').toUpperCase();
            if (decision.includes('GRADUATE')) {
                await this.graduate(row);
            } else if (decision.includes('KEEP') || decision.includes('ITERATE') || decision.includes('KILL')) {
                const d = decision.includes('KEEP') ? 'KEEP' : decision.includes('ITERATE') ? 'ITERATE' : 'KILL';
                await this.decide(row, d);
            }
        }
        this.bulkBusy = false;
        this.cdr.detectChanges();
    }

    async generatePlan(): Promise<void> {
        if (!this.currentSprint) return;
        this.planBusy = true;
        this.planError = '';
        this.cdr.detectChanges();
        const res = await runAction('Generate Sprint Plan', [
            { Name: 'CompanyID', Value: this._companyId },
            { Name: 'SprintID', Value: this.currentSprint.ID },
        ]);
        this.planBusy = false;
        if (res.success) {
            this.planDoc = res.message || '';
            this.planEditing = false;
        } else {
            this.planError = `Generate failed (${res.resultCode}): ${res.message}`;
        }
        this.cdr.detectChanges();
    }

    async savePlan(): Promise<void> {
        if (!this.currentSprint) return;
        this.planBusy = true;
        this.planError = '';
        this.cdr.detectChanges();
        try {
            const md = new Metadata();
            const sprint = await md.GetEntityObject<marketingSprintEntity>('Sprints');
            await sprint.Load(this.currentSprint.ID);
            sprint.PlanningDoc = this.planDraft;
            const ok = await sprint.Save();
            if (!ok) {
                this.planError = `Save failed: ${sprint.LatestResult?.Message ?? 'unknown'}`;
            } else {
                this.planDoc = this.planDraft;
                this.planEditing = false;
                this.currentSprint.PlanningDoc = this.planDraft;
            }
        } catch (err: unknown) {
            this.planError = err instanceof Error ? err.message : String(err);
        } finally {
            this.planBusy = false;
            this.cdr.detectChanges();
        }
    }
}
